import { inject, injectable, named } from 'inversify';
import { fetchAuthSession } from '@aws-amplify/auth';

import type {
  AddAdyenPaymentDetails,
  CreateOrder,
  DeletePaymentMethod,
  FinalizeAdyenPaymentDetails,
  GetAdyenPaymentSession,
  GetEntitlements,
  GetFinalizeAdyenPayment,
  GetInitialAdyenPayment,
  GetMediaOffers,
  GetOffer,
  GetOffers,
  GetOrder,
  GetPaymentMethods,
  GetSubscriptionOffers,
  GetSubscriptionSwitch,
  GetSubscriptionSwitches,
  Offer,
  PaymentWithoutDetails,
  PaymentWithPayPal,
  SwitchSubscription,
  UpdateOrder,
  UpdatePaymentWithPayPal,
} from '../../../../types/checkout';
import CheckoutService from '../CheckoutService';
import env from '../../../env';
import AccountService from '../AccountService';
import { createURL } from '../../../utils/urlFormatting';

import type AWSCognitoAccountService from './AWSCognitoAccountService';

@injectable()
export default class AWSStripeCheckoutService extends CheckoutService {
  constructor(@inject(AccountService) @named('COGNITO_STRIPE') private readonly accountService: AWSCognitoAccountService) {
    super();
  }

  protected transformOffer = (offer: Offer) => {
    const invoiceMode = this.accountService.invoiceMode;
    const coupon = this.accountService.coupon;
    const promotionCode = this.accountService.promotionCode;

    if (offer.offerUrl) {
      return {
        ...offer,
        type: 'subscription' as const, // we currently only support subscription type
        offerUrl: createURL(offer.offerUrl, {
          ...(invoiceMode ? { invoiceMode: 'true' } : {}),
          coupon,
          promotionCode,
        }),
      };
    }

    return offer;
  };

  getOffers: GetOffers = async ({ offerIds, customerCountry }) => {
    const offers = await Promise.all(
      offerIds.map(async (offerId) => {
        const response = await fetch(
          createURL(`${env.APP_ACCOUNT_API_BASE_URL}/product-offers`, {
            id: offerId,
            country: customerCountry,
          }),
        );

        const data = (await response.json()) as { offers: Offer[] };

        return data.offers.map(this.transformOffer);
      }),
    );

    return offers.flat();
  };

  getOffer: GetOffer = async ({ offerId, customerCountry }) => {
    const response = await fetch(
      createURL(`${env.APP_ACCOUNT_API_BASE_URL}/product-offers`, {
        id: offerId,
        country: customerCountry,
      }),
    );
    const data = await response.json();
    const offer = data.offers[0] as Offer;

    return { responseData: this.transformOffer(offer), errors: [] };
  };

  getSubscriptionOffers: GetSubscriptionOffers = async ({ customerCountry }) => {
    const stripeProducts = this.accountService.stripeProducts || [];

    const offers = await Promise.all(
      stripeProducts.map(async ({ id, label }) => {
        const fetchedOffers = await this.getOffers({ offerIds: [id], customerCountry });
        return fetchedOffers.map((offer) => {
          return {
            ...offer,
            offerGroup: { id, label },
          };
        });
      }),
    );

    return offers.flat();
  };

  getMediaOffers: GetMediaOffers | undefined = undefined;

  createOrder: CreateOrder = async () => {
    throw new Error('Not supported');
  };

  getOrder: GetOrder = async () => {
    throw new Error('Not supported');
  };

  updateOrder: UpdateOrder = async () => {
    throw new Error('Not supported');
  };

  getPaymentMethods: GetPaymentMethods = async () => {
    throw new Error('Not supported');
  };

  paymentWithoutDetails: PaymentWithoutDetails = async () => {
    throw new Error('Not supported');
  };

  paymentWithPayPal: PaymentWithPayPal = async () => {
    throw new Error('Not supported');
  };

  getSubscriptionSwitches: GetSubscriptionSwitches = async () => {
    return { responseData: { available: [], unavailable: [] }, errors: [] };
  };

  getSubscriptionSwitch: GetSubscriptionSwitch = async () => {
    throw new Error('Not supported');
  };

  switchSubscription: SwitchSubscription = async () => {
    throw new Error('Not supported');
  };

  getEntitlements: GetEntitlements = async () => {
    const user = await fetchAuthSession();
    const response = await fetch(`${env.APP_ACCOUNT_API_BASE_URL}/get-access`, {
      headers: { Authorization: `Bearer ${user.tokens?.idToken?.toString()}` },
    });
    const data = await response.json();

    return { responseData: data, errors: [] };
  };

  createAdyenPaymentSession: GetAdyenPaymentSession = async () => {
    throw new Error('Not supported');
  };

  initialAdyenPayment: GetInitialAdyenPayment = async () => {
    throw new Error('Not supported');
  };

  finalizeAdyenPayment: GetFinalizeAdyenPayment = async () => {
    throw new Error('Not supported');
  };

  updatePaymentMethodWithPayPal: UpdatePaymentWithPayPal = async () => {
    throw new Error('Not supported');
  };

  deletePaymentMethod: DeletePaymentMethod = async () => {
    throw new Error('Not supported');
  };

  addAdyenPaymentDetails: AddAdyenPaymentDetails = async () => {
    throw new Error('Not supported');
  };

  finalizeAdyenPaymentDetails: FinalizeAdyenPaymentDetails = async () => {
    throw new Error('Not supported');
  };

  directPostCardPayment = async () => false;

  /**
   * VD Private code below
   */
  forwardStoreReceipt = async () => {
    throw new Error('Not supported');
  };
}
